import React from 'react';
import { Helmet } from 'react-helmet';
import style from './Organisations.module.scss';

const CONTACT_EMAIL = 'info@yesterdaystories.com.au';
const CONTACT_HREF = `mailto:${CONTACT_EMAIL}?subject=Branded%20trail%20enquiry`;

const STEPS = [
    {
        title: 'We plan the trail with you',
        body: 'We sit down with your heritage, tourism and community teams to pick the stops and the stories worth telling.',
    },
    {
        title: 'We film the stories',
        body: 'Our documentary team films short histories on location with local historians, Elders, long-time residents and the places themselves. Already have footage? We can use it.',
    },
    {
        title: 'We put your name on it',
        body: 'Your logo, your colours, your trail name. Visitors see your organisation first.',
    },
    {
        title: 'We launch and support it',
        body: 'QR codes for your signage, a map you can embed on your tourism website, and visitor reports for your own reporting.',
    },
];

const TIERS = [
    {
        tag: 'Option 1',
        title: 'Partner Trail',
        body: 'Your trail as a branded collection inside the Yesterday Stories app.',
        points: [
            'Your logo and trail page in the app',
            'QR codes for trail signage',
            'Reaches people already using the Yesterday Stories app',
        ],
        best: 'Best for: a first trail, a festival or a small budget.',
    },
    {
        tag: 'Option 2 · Most popular',
        title: 'Branded Web App',
        body: 'A trail app on your own web address. It opens in any phone browser with nothing to download.',
        points: [
            'Your name, colours and domain',
            'Interactive map to embed on your website',
            'Visitor analytics dashboard',
        ],
        best: 'Best for: councils and tourism teams with several trails.',
        featured: true,
    },
    {
        tag: 'Option 3',
        title: 'Branded Mobile App',
        body: "Your own app on the Apple App Store and Google Play, published under your organisation's name.",
        points: [
            'Everything in the Branded Web App',
            'Push notifications for events and closures',
            'Listed in the app stores as yours',
        ],
        best: 'Best for: regions building a long-term visitor app.',
    },
];

const INCLUDED = [
    {
        title: 'Location-based stories',
        body: 'Films tied to the exact spot where the history happened, shown on a map.',
    },
    {
        title: 'Walking, driving or cycling routes',
        body: 'Self-guided, at any pace.',
    },
    {
        title: 'QR signage and website map',
        body: 'QR codes for each stop and a map you can embed on your own website.',
    },
    {
        title: 'Visitor reports',
        body: 'See which stories and stops visitors watch.',
    },
    {
        title: 'Two audiences, not one',
        body: 'Your stories also appear in the Yesterday Stories app, so travellers from outside your area find them too.',
    },
];

const WHY = [
    {
        title: 'Documentary-quality stories',
        body: 'Our founder, Sandra Pires, is a history documentary filmmaker. Every trail film is made to the same standard.',
    },
    {
        title: 'Already telling regional NSW stories',
        body: 'Stories are live across the Illawarra, Wollondilly, Wingecarribee, Goulburn Mulwaree, Hilltops and beyond. Trails like the Mount Kembla Trail show how it works on the ground.',
    },
    {
        title: 'Communities tell their own history',
        body: 'We run filmmaking workshops with schools and community groups, so local people help make the films. Our stories also reach classrooms through ClickView.',
    },
    {
        title: 'Funded by NSW Government',
        body: 'Yesterday Stories is funded by NSW Government in association with Wollongong City Council.',
    },
];

const AUDIENCES = [
    'Local councils',
    'Tourism teams',
    'Museums & galleries',
    'Historical societies',
    'National parks & reserves',
    'Aboriginal Land Councils',
    'Heritage properties',
    'Visitor centres',
];

const FAQS = [
    {
        q: 'Do visitors need to download anything?',
        a: 'Not with the Branded Web App. It opens straight from a QR code or link in any phone browser. The Partner Trail and the Branded Mobile App use an app from the App Store or Google Play.',
    },
    {
        q: 'What does it cost?',
        a: "It depends on the number of stops, how many new films you need and which option you choose. Tell us about your trail and we'll send a quote.",
    },
];

const Organisations = () => (
    <>
        <Helmet>
            <title>Branded Trail Apps for Councils and Museums | Yesterday Stories</title>
            <meta name="description" content="Your own branded trail app, filled with short films about the people and places of your region. Made by filmmakers for councils, museums and heritage groups." />
            <meta name="robots" content="index, follow" />
        </Helmet>

        <section className={style.hero}>
            <div className={style.heroGrid} aria-hidden="true" />
            <div className={`wrap ${style.heroInner}`}>
                <div>
                    <div className={style.heroEyebrow}>For councils, museums &amp; heritage groups</div>
                    <h1 className={style.title}>
                        Your trails.<br />Your brand.<br /><em>Stories people</em> stop for.
                    </h1>
                    <p className={style.sub}>
                        We build trail apps under your name and fill them with short films about the people and places
                        along the way. You get a finished trail, not an empty app builder.
                    </p>
                    <div className={style.actions}>
                        <a href={CONTACT_HREF} className={style.btnPrimary}>Get in touch</a>
                        <a href="#options" className={style.btnGhost}>See the options</a>
                    </div>
                </div>
                <div className={style.phoneWrap} aria-hidden="true">
                    <div className={style.phone}>
                        <div className={style.screen}>
                            <div className={style.scrTop}>
                                <div className={style.scrLogo}><span className={style.scrBadge}>LOGO</span>Your organisation</div>
                                <div className={style.scrTitle}>Example heritage walk</div>
                                <div className={style.scrMeta}>5 STOPS · 3 KM · ABOUT 45 MIN</div>
                            </div>
                            <div className={style.scrMap} />
                            <div className={style.scrList}>
                                <div className={style.scrStop}><i>1</i><div><b>Stop one</b><small>2:40 · Industrial</small></div></div>
                                <div className={style.scrStop}><i>2</i><div><b>Stop two</b><small>3:15 · Our communities</small></div></div>
                                <div className={style.scrStop}><i>3</i><div><b>Stop three</b><small>4:02 · Sites &amp; landmarks</small></div></div>
                            </div>
                            <div className={style.scrPlay}>▶ Start trail</div>
                        </div>
                    </div>
                    <div className={style.phoneCaption}>Illustration only</div>
                </div>
            </div>
        </section>

        <section className={style.light} id="how-it-works-organisations">
            <div className="wrap">
                <div className="eyebrow">How it works</div>
                <h2 className={style.wideHeading}>Most trail apps arrive empty. Ours arrive with the stories.</h2>
                <p className={style.lede}>
                    Other platforms hand you a login and a blank map. Then someone on your team has to research,
                    write, record and upload every stop, and that is where most trail projects stall. We start with
                    the stories.
                </p>
                <div className={style.steps}>
                    {STEPS.map((step, i) => (
                        <div className={style.step} key={step.title}>
                            <div className={style.stepNum}>{String(i + 1).padStart(2, '0')}</div>
                            <h3>{step.title}</h3>
                            <p>{step.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        <section className={style.sand} id="options">
            <div className="wrap">
                <div className="eyebrow">Choose your level</div>
                <h2 className={style.wideHeading}>Start small, or go all the way to <em>your own app.</em></h2>
                <div className={style.tiers}>
                    {TIERS.map(tier => (
                        <div className={`${style.tier} ${tier.featured ? style.tierFeatured : ''}`} key={tier.title}>
                            <span className={style.tierTag}>{tier.tag}</span>
                            <h3>{tier.title}</h3>
                            <p>{tier.body}</p>
                            <ul>
                                {tier.points.map(point => <li key={point}>{point}</li>)}
                            </ul>
                            <div className={style.tierBest}>{tier.best}</div>
                        </div>
                    ))}
                </div>
                <p className={style.note}>Every option can include our story production and community filmmaking services.</p>
                <p className={style.note}>
                    Branded web and mobile app options are being developed. Get in touch to talk about what suits
                    your project and when.
                </p>
            </div>
        </section>

        <section className={`${style.dark} ${style.included}`}>
            <div className={`wrap ${style.split}`}>
                <div>
                    <div className="eyebrow">What's included</div>
                    <h2>Everything a trail needs, from the first film to the last stop.</h2>
                    <p className={style.lede}>
                        A trail strings a handful of stories into one route. Visitors follow the pins from stop to
                        stop, press play, and your region's history unfolds where it happened.
                    </p>
                    <div className={style.stats}>
                        <div className={style.stat}><b>4–7</b><span>Stops per trail</span></div>
                        <div className={style.stat}><b>~2–6 km</b><span>Typical length</span></div>
                        <div className={style.stat}><b>3–5 min</b><span>Per film</span></div>
                    </div>
                    <a href={CONTACT_HREF} className={style.btnPrimary}>Talk to us about your trail</a>
                </div>
                <div className={style.panel}>
                    <div className="eyebrow">Included in every trail</div>
                    <h3>The full kit</h3>
                    <ul className={style.incl}>
                        {INCLUDED.map((item, i) => (
                            <li key={item.title}>
                                <i>{i + 1}</i>
                                <div><b>{item.title}</b><small>{item.body}</small></div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>

        <section className={style.light}>
            <div className="wrap">
                <div className="eyebrow">Why Yesterday Stories</div>
                <h2 className={style.wideHeading}>Made by filmmakers, not just software people.</h2>
                <div className={style.why}>
                    {WHY.map(item => (
                        <div key={item.title}>
                            <h3>{item.title}</h3>
                            <p>{item.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        <section className={`${style.dark} ${style.quoteSection}`}>
            <div className={`wrap ${style.quote}`}>
                <div className="eyebrow">What people are saying</div>
                <blockquote>“I love what you are doing, it’s such a gap in the market, and so authentic.”</blockquote>
                <cite>Business and Investment Attraction Officer (Tourism)</cite>
            </div>
        </section>

        <section className={style.light}>
            <div className={`wrap ${style.split}`}>
                <div>
                    <div className="eyebrow">Who it's for</div>
                    <h2>Built for the people who look after a place's history.</h2>
                    <div className={style.who}>
                        {AUDIENCES.map(name => <span key={name}>{name}</span>)}
                    </div>
                </div>
                <div className={style.fund}>
                    <div className="eyebrow">Funding your trail</div>
                    <h3>Grants may cover the stories.</h3>
                    <p>
                        Heritage, community and tourism grants may cover story and film production. Tell us about your
                        project and we can help you scope it.
                    </p>
                    <a className={style.txtLink} href={CONTACT_HREF}>Ask us about grants</a>
                </div>
            </div>
        </section>

        <section className={style.sand}>
            <div className="wrap">
                <div className="eyebrow">Questions</div>
                <h2>Common questions</h2>
                <div className={style.faq}>
                    {FAQS.map(item => (
                        <details key={item.q}>
                            <summary>{item.q}</summary>
                            <p>{item.a}</p>
                        </details>
                    ))}
                </div>
            </div>
        </section>

        <section className={`${style.light} ${style.cta}`} id="contact">
            <div className="wrap">
                <div className="eyebrow" style={{ justifyContent: 'center' }}>Let's talk</div>
                <h2>Let's put your stories <em>on the map.</em></h2>
                <p className={style.lede}>
                    Tell us about your trail and we'll show you what it could look like under your name.
                </p>
                <div className={style.ctaActions}>
                    <a href={CONTACT_HREF} className={style.btnPrimary}>Get in touch</a>
                    <a href={`mailto:${CONTACT_EMAIL}`} className={style.btnGhostDark}>{CONTACT_EMAIL}</a>
                </div>
            </div>
        </section>
    </>
);

export default Organisations;
